import React, { Fragment, useMemo, useRef, useState } from "react";

import { Combobox, Transition } from "@headlessui/react";
import { Check, ChevronDown, Globe, Search } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "../../../lib/utils";
import {
  filterCountryOptions,
  findCountryOption,
  getCountryOptions,
} from "../utils/countryOptions";

const CountryFlag = ({ code, src, className = "" }) => {
  const [failed, setFailed] = useState(false);

  if (!code) {
    return null;
  }

  if (!src || failed) {
    return (
      <span
        aria-hidden="true"
        className={cn(
          "inline-flex h-[15px] w-5 shrink-0 items-center justify-center rounded-[3px] bg-cfInactiveSurface text-[8px] font-semibold leading-none text-cfTextBreadcrumbDisabled",
          className,
        )}
      >
        {code}
      </span>
    );
  }

  return (
    <img
      src={src}
      alt=""
      aria-hidden="true"
      loading="lazy"
      decoding="async"
      width={20}
      height={15}
      onError={() => setFailed(true)}
      className={cn(
        "h-[15px] w-5 shrink-0 rounded-[3px] object-cover shadow-[0_0_0_1px_rgba(16,24,40,0.12)]",
        className,
      )}
    />
  );
};

const ACTIVE_ROW = "bg-[color-mix(in_srgb,var(--cf-primary-color)_8%,white)]";
const FOCUS_RING =
  "focus-within:border-cfPrimaryButton focus-within:shadow-[0_0_0_3px_color-mix(in_srgb,var(--cf-primary-color)_18%,transparent)]";

const CountrySelect = ({
  id,
  value,
  onChange,
  invalid,
  disabled,
  describedBy,
}) => {
  const { t, i18n } = useTranslation(["onboarding"]);
  const language = i18n?.language || "en";
  const [query, setQuery] = useState("");
  const buttonRef = useRef(null);

  const options = useMemo(() => getCountryOptions(language), [language]);
  const selected = useMemo(
    () => findCountryOption(value, language),
    [value, language],
  );
  const filtered = useMemo(
    () => filterCountryOptions(options, query),
    [options, query],
  );

  return (
    <Combobox
      value={selected}
      by="code"
      disabled={disabled}
      onChange={(option) => {
        setQuery("");
        if (option) {
          onChange(option.value);
        }
      }}
    >
      {({ open }) => (
        <div className="relative">
          <div
            className={cn(
              "relative flex items-center rounded-[8px] border-[1px] bg-cfSecondary transition-[border-color,box-shadow] duration-150",
              invalid
                ? "border-cfErrorText"
                : "border-cfSignInSignUpInputBorder",
              !invalid && FOCUS_RING,
              disabled && "opacity-60",
            )}
          >
            <span className="pointer-events-none flex w-10 shrink-0 items-center justify-center">
              {selected && !query ? (
                <CountryFlag code={selected.code} src={selected.flag} />
              ) : open ? (
                <Search
                  className="h-4 w-4 text-cfTextBreadcrumbDisabled"
                  aria-hidden="true"
                />
              ) : (
                <Globe
                  className="h-4 w-4 text-cfTextBreadcrumbDisabled"
                  aria-hidden="true"
                />
              )}
            </span>

            <Combobox.Input
              id={id}
              autoComplete="off"
              aria-invalid={invalid || undefined}
              aria-describedby={describedBy}
              placeholder={
                open
                  ? t("onboarding:profile_country_search")
                  : t("onboarding:profile_country_placeholder")
              }
              displayValue={(option) => option?.label || ""}
              onChange={(event) => setQuery(event.target.value)}
              onClick={() => {
                if (!open) buttonRef.current?.click();
              }}
              className="h-[42px] w-full min-w-0 text-sm text-cfHeadingText placeholder:text-cfSignInSignUpInputPlaceholderText"
              style={{
                border: 0,
                padding: "0 2.5rem 0 0",
                background: "transparent",
                outline: "none",
                boxShadow: "none",
              }}
            />

            <Combobox.Button
              ref={buttonRef}
              className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-cfTextBreadcrumbDisabled"
              aria-label={t("onboarding:profile_country_toggle")}
              style={{ outline: "none" }}
            >
              <ChevronDown
                className={cn(
                  "h-4 w-4 transition-transform duration-200",
                  open && "rotate-180",
                )}
                aria-hidden="true"
              />
            </Combobox.Button>
          </div>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-150"
            enterFrom="opacity-0 -translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery("")}
          >
            <Combobox.Options className="absolute z-30 mt-1.5 max-h-72 w-full overflow-y-auto overscroll-contain rounded-xl border-[1px] border-cfCardBorder bg-cfSecondary p-1.5 shadow-[0_12px_32px_-8px_rgba(16,24,40,0.18)] focus:outline-none">
              {filtered.length === 0 ? (
                <li className="px-[12px] py-6 text-center text-sm text-cfGlobalText">
                  {t("onboarding:profile_country_empty", { query })}
                </li>
              ) : (
                filtered.map((option) => (
                  <Combobox.Option
                    key={option.code}
                    value={option}
                    id={`onboarding-country-option-${option.code}`}
                    className={({ active }) =>
                      cn(
                        "flex cursor-pointer select-none items-center gap-3 rounded-[8px] px-2.5 py-2 text-sm transition-colors duration-100",
                        active ? ACTIVE_ROW : "bg-transparent",
                      )
                    }
                  >
                    {({ selected: isSelected }) => (
                      <>
                        <CountryFlag code={option.code} src={option.flag} />
                        <span
                          className={cn(
                            "min-w-0 flex-1 truncate",
                            isSelected
                              ? "font-semibold text-cfHeadingText"
                              : "text-cfCardTagText",
                          )}
                        >
                          {option.label}
                        </span>
                        {isSelected ? (
                          <Check
                            className="h-4 w-4 shrink-0 text-cfPrimaryButton"
                            strokeWidth={2.5}
                            aria-hidden="true"
                          />
                        ) : null}
                      </>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
        </div>
      )}
    </Combobox>
  );
};

export default CountrySelect;
